import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BatteryFullIcon, BuildingIcon, CheckIcon, RefreshCwIcon, TruckIcon } from 'lucide-react';
import { ObjectionId, objectionChoices, RECOVERY_ID } from '../data/objections';
import { offers, SHIPPING_LINE } from '../data/product';
import { deepLinkClick } from '../utils/deepLinks';
import { CtaButton } from './CtaButton';
import { LifetimeSeal } from './LifetimeSeal';
import { useTrackOnView } from '../hooks/useTrackOnView';
import { trackCustom, packEvents } from '../utils/tracking';
import { requestOffer, PackId } from '../utils/offerBus';
interface ObjectionRecoveryProps {
  /** The objection the visitor picked. Nothing renders until they pick one. */
  active: ObjectionId | null;
  /** Reopens the selector. */
  onChooseAnother: () => void;
}
const headlineClass = 'grit-headline text-[clamp(2.25rem,8vw,4.5rem)] font-extrabold uppercase leading-[0.92] text-offwhite';
const subClass = 'mt-5 max-w-2xl text-[15px] leading-relaxed text-steel-300 sm:text-lg';
const statementClass = 'mt-6 border-l-4 border-grit-500 pl-4 font-display text-2xl font-bold uppercase leading-tight text-offwhite sm:text-3xl';
function Bullets({
  items
}: {
  items: string[];
}) {
  return <ul className="mt-6 grid gap-2.5" data-id="element-736" data-mp="29vqvqr2ms667">
      {items.map(line => <li key={line} className="flex items-start gap-2.5 text-[15px] font-medium text-offwhite" data-id="element-737" data-mp="2xm4mme9hbyz7">
          <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-738" data-mp="32l7s1ztdg77p" />
          {line}
        </li>)}
    </ul>;
}
const scrollToCompany = () => {
  document.getElementById('company')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
};

/** The public packs only. Private pricing never appears here. */
function PackClose({
  recommended,
  cta
}: {
  recommended: PackId;
  cta: string;
}) {
  return <div className="mt-10 border-t border-white/10 pt-8" data-id="element-739" data-mp="3lbz3r80u09p9">
      <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-grit-500" data-id="element-740" data-mp="3ldjsgh0gsyf7">Recommended for you</p>
      <ul className="mt-4 grid gap-3 sm:grid-cols-3" data-id="element-741" data-mp="3q8l4et1a6epe">
        {offers.map(offer => {
        const isPick = offer.id === recommended;
        return <li key={offer.id} data-id="element-742" data-mp="3m56e99jmohy8">
              <a href="#offer" data-buy-intent="true" onClick={event => {
            event.preventDefault();
            requestOffer(offer.id);
            trackCustom(packEvents[offer.id].selected);
            trackCustom('mini_funnel_cta_clicked', {
              pack: offer.id
            });
          }} className={`flex h-full flex-col border p-4 transition-[border-color,background-color] duration-150 ease-grit focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500 ${isPick ? 'border-grit-500 bg-grit-500/10 hover:bg-grit-500/15' : 'border-white/15 bg-ink-900 hover:border-white/35'}`} data-id="element-743" data-mp="3h7wr60jpoidk">
                <span className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-744" data-mp="141zuo7z6s9bt">
                  {offer.eyebrow ?? offer.kicker}
                </span>
                <span className="font-display text-2xl font-bold uppercase leading-none text-offwhite" data-id="element-745" data-mp="1dzns55o6fuxo">{offer.units}</span>
                <span className="mt-2 font-display text-3xl font-extrabold leading-none text-offwhite" data-id="element-746" data-mp="1i198x23u0td5">{offer.price}</span>
                <span className="mt-1 text-[12px] font-semibold text-steel-400" data-id="element-747" data-mp="lca1tqezhxmj">
                  {offer.perUnit ?? 'One-year coverage · lifetime available'}
                </span>
                <span className="mt-auto pt-3 text-[12px] font-semibold text-steel-400" data-id="element-748" data-mp="uac7wkdkrggo">
                  Free U.S. shipping
                </span>
              </a>
            </li>;
      })}
      </ul>
      <CtaButton offer={recommended} className="mt-6 w-full sm:w-auto" onClick={() => trackCustom('mini_funnel_cta_clicked')} data-id="element-749" data-mp="2jfthfbv93ra">
        {cta}
      </CtaButton>
    </div>;
}
function PriceFunnel() {
  const splitRef = useTrackOnView<HTMLDivElement>('price_split_offer_viewed');
  return <>
      <h2 className={headlineClass} data-id="element-750" data-mp="3eotshtfrcj4d">
        Here&apos;s what
        <br data-id="element-751" data-mp="3tcwxazi2dorl" />
        <span className="text-grit-500" data-id="element-752" data-mp="3tcn30zpzkvg3">$49.95 gets you.</span>
      </h2>
      <p className={subClass} data-id="element-753" data-mp="3olydvr1s4ak4">
        Not a pair of glasses. A complete work system: light, three lenses and everything to carry and care for it.
      </p>
      <div className="mt-8 grid gap-8 lg:grid-cols-2" data-id="element-754" data-mp="664cvmxuecxa">
        <Bullets items={['GritVue X1 frame with built-in 800-lumen light', '3 interchangeable lenses: Clear, Amber, Smoke', 'Hard protective case AV8, protective pouch and cleaning cloth', 'Charging cable and GritVue Quick Start Guide', 'One-year replacement coverage, or Lifetime on multi-packs']} data-id="element-755" data-mp="1944h5disy30" />
        <div ref={splitRef} className="border border-white/12 bg-ink-900 p-6" data-id="element-756" data-mp="2cyewufpiwyqm">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-757" data-mp="sg4bqhkgwyma">Cost per X1</p>
          <dl className="mt-4 grid gap-2.5 text-[15px]" data-id="element-758" data-mp="xf7ge0wjd2tq">
            {[['1 X1', '$49.95'], ['3 X1 · Buy 2, get 1 free', '$33.30 each'], ['6 X1 · Buy 4, get 2 free', '$29.98 each'], ['30 X1 · Buy 20, get 10 free', '$23.33 each']].map(([label, value]) => <div key={label} className="flex items-baseline justify-between gap-4 border-b border-white/5 pb-2" data-id="element-759" data-mp="9pd4pmnydkb2">
                <dt className="text-steel-300" data-id="element-760" data-mp="drzrh95rof1e">{label}</dt>
                <dd className="font-display text-2xl font-extrabold text-offwhite" data-id="element-761" data-mp="3vx2yx0j7h34z">{value}</dd>
              </div>)}
          </dl>
          <p className="mt-4 text-[14px] leading-relaxed text-steel-300" data-id="element-762" data-mp="4rbdufkacgp1">
            Work with a crew? Split a 3-pack with two coworkers and each of you pays about $33.30, with lifetime coverage
            on all three.
          </p>
        </div>
      </div>
      <CtaButton offer="trio" className="mt-8 w-full sm:w-auto" onClick={() => {
      trackCustom('price_split_clicked');
      trackCustom('mini_funnel_cta_clicked');
    }} data-id="element-763" data-mp="1xn48pj2bjet6">
        See the best value for me
      </CtaButton>
    </>;
}
function PrescriptionFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-764" data-mp="1irerrzbzprvb">
        Wear prescription lenses?
        <br data-id="element-765" data-mp="2geeslg9mmrbk" />
        <span className="text-grit-500" data-id="element-766" data-mp="2gctzlv2yngft">Your X1 is prescription ready.</span>
      </h2>
      <p className={subClass} data-id="element-767" data-mp="2be91c179r6u0">
        Available now: RX Capability Insert, +$14.95. Your optical professional fits your prescription lenses into it.
      </p>
      <div className="mt-8 grid gap-4 lg:grid-cols-2" data-id="element-768" data-mp="26lzfaw99yqx0">
        <div className="border-2 border-grit-500 bg-grit-500/10 p-6" data-id="element-769" data-mp="21nxwmcfrgtm2">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-grit-500" data-id="element-770" data-mp="2z43d0ffuqpco">Available now</p>
          <p className="mt-2 font-display text-2xl font-bold uppercase leading-tight text-offwhite" data-id="element-771" data-mp="2ud4q2x0cyt0g">Prescription ready</p>
          <p className="mt-2 text-[15px] leading-relaxed text-steel-300" data-id="element-772" data-mp="32nc60ihp82s1">Your vision. Your prescription. Your X1.</p>
        </div>
        <div className="border border-white/12 bg-ink-900 p-6" data-id="element-773" data-mp="36o5r743rb3h8">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-774" data-mp="3blxjhz8bcrly">Add-on · +$14.95</p>
          <p className="mt-2 font-display text-2xl font-bold uppercase leading-tight text-offwhite" data-id="element-775" data-mp="3gmv047doddlc">GritVue RX Capability Insert™</p>
          <p className="mt-2 text-[15px] leading-relaxed text-steel-300" data-id="element-776" data-mp="3lj26u2dwat4c">
            A removable insert that sits behind the main lens, so your prescription stays in place while you switch
            Clear, Amber and Smoke. Your optical professional fits the prescription lenses; they are not included.
          </p>
          <a href="#rx" onClick={deepLinkClick('rx')} className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.18em] text-grit-500 underline-offset-4 hover:underline" data-id="element-777" data-mp="868jhaew4av4">
            See how RX works
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-778" data-mp="yi4mtptya7fi" />
          </a>
        </div>
      </div>
      <p className="mt-3 text-[12px] text-steel-500" data-id="element-779" data-mp="tjjipfbkv3ls">
        <Link to="/legal/terms" className="underline" data-id="element-780" data-mp="17foqt6j76htt">
          Terms apply.
        </Link>
      </p>
      <CtaButton offer="single" className="mt-8 w-full sm:w-auto" onClick={() => trackCustom('mini_funnel_cta_clicked')} data-id="element-781" data-mp="12j6nmb3imrjl">
        Get my X1
      </CtaButton>
    </>;
}
function BatteryFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-782" data-mp="ask0qy2i2u20">
        Light that lasts
        <br data-id="element-783" data-mp="oidunusv44il" />
        <span className="text-grit-500" data-id="element-784" data-mp="oivut2inb2vs">the whole job.</span>
      </h2>
      <p className={subClass} data-id="element-785" data-mp="jqvp0tfw9fra">
        One X1 gives about 3 hours 20 minutes of light per full charge and recharges over USB-C in about 2.5 hours.
      </p>
      <div className="mt-8 grid gap-3 sm:grid-cols-2" data-id="element-786" data-mp="3s54a7gg3749f">
        {[{
        label: 'Runtime per charge',
        value: 'About 3h 20m'
      }, {
        label: 'Full charge',
        value: 'About 2.5 hours'
      }].map(mode => <div key={mode.label} className="flex items-center gap-4 border border-white/12 bg-ink-900 px-5 py-5" data-id="element-787" data-mp="27gs8awh5e9td">
            <BatteryFullIcon className="h-8 w-8 shrink-0 text-grit-500" aria-hidden="true" data-id="element-788" data-mp="1tpx4hyliaiwn" />
            <div data-id="element-789" data-mp="1yo8cs4w5klcr">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-790" data-mp="1ynoycy5pof3h">{mode.label}</p>
              <p className="font-display text-2xl font-bold uppercase text-offwhite" data-id="element-791" data-mp="2mf43eyovhaif">{mode.value}</p>
            </div>
          </div>)}
      </div>
      <p className={statementClass} data-id="element-792" data-mp="2cfvckepb0c0a">
        Long shift?
        <br data-id="element-793" data-mp="2cfd6yrgr4lfp" />
        Keep a second X1 charged and swap.
      </p>
      <p className="mt-3 text-[12px] text-steel-500" data-id="element-794" data-mp="2hega26d68hl1">Runtime varies by light mode, battery age, temperature and use.</p>
    </>;
}
function FitFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-795" data-mp="15zt2twrvot8a">
        You&apos;ll forget
        <br data-id="element-796" data-mp="16145ft9l7uxt" />
        <span className="text-grit-500" data-id="element-797" data-mp="15xq2ait3nhpb">you&apos;re wearing it.</span>
      </h2>
      <p className={subClass} data-id="element-798" data-mp="3g82hhk839k2p">The light is built into the frame, so there is nothing strapped around your head.</p>
      <Bullets items={['1.4 oz (40 g), lighter than most safety glasses', 'No head strap, no battery pack on your neck', 'Wave-on motion sensor. No hunting for the button with gloves on']} data-id="element-799" data-mp="3l8q5ry4xnmt8" />
    </>;
}
function ShippingFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-800" data-mp="2xjfdz65k92r8">
        Shipping,
        <br data-id="element-801" data-mp="31jpmapgts599" />
        <span className="text-grit-500" data-id="element-802" data-mp="31k7os3u2zfpm">straight answers.</span>
      </h2>
      <div className="mt-8 grid gap-4 lg:grid-cols-2" data-id="element-803" data-mp="2njofktdvq3lv">
        <div className="border-2 border-grit-500 bg-grit-500/10 p-6 lg:col-span-2" data-id="element-804" data-mp="2nl80wyee74ui">
          <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-grit-500" data-id="element-805" data-mp="2sk2ftp98o99c">
            <TruckIcon className="h-4 w-4" aria-hidden="true" data-id="element-806" data-mp="lppawqv9iki5" />
            Every order
          </p>
          <p className="mt-2 font-display text-2xl font-bold uppercase leading-tight text-offwhite" data-id="element-807" data-mp="qo8syg6qjc58">{SHIPPING_LINE}</p>
          <p className="mt-2 text-[15px] text-steel-300" data-id="element-808" data-mp="269swfmkc88m1">
            X1 Essential builds from $29.95, the Complete System and every multi-pack.
          </p>
        </div>
      </div>
      <p className="mt-4 text-[14px] text-steel-400" data-id="element-809" data-mp="2g7qluddxobg8">
        USPS, insured and tracked, from the U.S. Orders placed before 2pm CT ship the same business day.
      </p>
      <CtaButton href="#build-your-x1" className="mt-8 w-full sm:w-auto" onClick={() => trackCustom('mini_funnel_cta_clicked')} data-id="element-810" data-mp="2txakb70hemjq">
        Build your X1 — from $29.95
      </CtaButton>
    </>;
}
function WarrantyFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-811" data-mp="2yyhtnai9vtsi">
        Break it.
        <br data-id="element-812" data-mp="2yyzvcmnpf07t" />
        <span className="text-grit-500" data-id="element-813" data-mp="2yw5fz9jjs5l9">We replace it.</span>
      </h2>
      <div className="mt-8 flex flex-col items-start gap-6 border border-grit-500/50 bg-ink-900 p-6 sm:flex-row sm:items-center sm:p-8" data-id="element-814" data-mp="19jtrs37b5u8q">
        <LifetimeSeal className="h-24 w-24 shrink-0" data-id="element-815" data-mp="1u8chzswcg3xc" />
        <dl className="grid flex-1 gap-3 text-[15px]" data-id="element-816" data-mp="1u9nvikflcq1n">
          <div data-id="element-817" data-mp="1y9oaq7ail59r">
            <dt className="font-display text-xl font-bold uppercase text-offwhite" data-id="element-818" data-mp="1y966andp7kqr">Single X1</dt>
            <dd className="text-steel-300" data-id="element-819" data-mp="238hnfxhz8s8v">One-year replacement coverage, or add the Lifetime Replacement Warranty.</dd>
          </div>
          <div data-id="element-820" data-mp="289owdzvuvxh4">
            <dt className="font-display text-xl font-bold uppercase text-offwhite" data-id="element-821" data-mp="286122yil771c">Every multi-pack</dt>
            <dd className="text-steel-300" data-id="element-822" data-mp="2d546qhunnb8s">Lifetime Replacement Warranty on every X1 in the pack.</dd>
          </div>
          <div data-id="element-823" data-mp="2lzikgfzjedps">
            <dt className="font-display text-xl font-bold uppercase text-offwhite" data-id="element-824" data-mp="2lz0j54y0p9b0">Making a claim</dt>
            <dd className="text-steel-300" data-id="element-825" data-mp="2qz4yusr3q006">
              One email with a photo. The replacement unit is free; a flat $6.95 shipping and handling fee applies per
              approved replacement claim.
            </dd>
          </div>
        </dl>
      </div>
      <Link to="/legal/guarantee" className="mt-4 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-grit-500 underline-offset-4 hover:underline" data-id="element-826" data-mp="30uoqv42joejn">
        View warranty terms
        <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-827" data-mp="faqcti93vzhs" />
      </Link>
    </>;
}
const recommendations: {
  label: string;
  body: string;
  pack: PackId | 'business';
  result: string;
}[] = [{
  label: 'Just for me',
  body: 'One X1 for your own work.',
  pack: 'single',
  result: '1 X1 · $49.95'
}, {
  label: 'One for work + backups',
  body: 'A spare charged and a spare in the truck.',
  pack: 'trio',
  result: '3 X1 · $99.90'
}, {
  label: 'Small crew',
  body: 'Outfit a few people at once.',
  pack: 'crew',
  result: '6 X1 · $179.90'
}, {
  label: 'Business / team',
  body: 'One order for the whole crew.',
  pack: 'business',
  result: '30 X1 · $699.90'
}];
function PackRecommender() {
  return <>
      <h2 className={headlineClass} data-id="element-828" data-mp="1lz6pzch4bb4w">
        Who is it
        <br data-id="element-829" data-mp="1lxlwzragc095" />
        <span className="text-grit-500" data-id="element-830" data-mp="1lxvndfzes9fd">for?</span>
      </h2>
      <p className={subClass} data-id="element-831" data-mp="16gvx6h05wcdv">Pick the one that sounds like you and we&apos;ll take you to that pack.</p>
      <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-832" data-mp="1bicygdmk60uz">
        {recommendations.map(item => <li key={item.label} data-id="element-833" data-mp="wlc9ghvr4k07">
            <a href={item.pack === 'business' ? '#company' : '#offer'} data-buy-intent="true" onClick={event => {
          trackCustom('pack_recommendation_selected', {
            choice: item.label,
            pack: item.pack
          });
          if (item.pack === 'business') {
            event.preventDefault();
            scrollToCompany();
            return;
          }
          event.preventDefault();
          requestOffer(item.pack);
        }} className="flex h-full flex-col border border-white/15 bg-ink-900 p-5 transition-[border-color,background-color] duration-150 ease-grit hover:border-grit-500 hover:bg-grit-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-834" data-mp="11icc0ite74mk">
              <span className="font-display text-xl font-bold uppercase leading-tight text-offwhite" data-id="element-835" data-mp="3spcz3vfrqhi4">{item.label}</span>
              <span className="mt-1.5 text-[14px] text-steel-300" data-id="element-836" data-mp="b8v64qbozdqa">{item.body}</span>
              <span className="mt-auto flex items-center justify-between gap-2 pt-4 font-display text-lg font-bold uppercase text-grit-500" data-id="element-837" data-mp="69qt1olncvuo">
                {item.result}
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-838" data-mp="3er57m31i48ec" />
              </span>
            </a>
          </li>)}
      </ul>
    </>;
}
function CrewFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-839" data-mp="3nnnnoeomw1ti">
        One order.
        <br data-id="element-840" data-mp="3jnlvqx8pamrf" />
        <span className="text-grit-500" data-id="element-841" data-mp="3jmu0xomxgt27">The whole crew.</span>
      </h2>
      <p className={subClass} data-id="element-842" data-mp="12yzuf02kokws">
        Every X1 in a multipack is a Complete System, with Lifetime Replacement Warranty on every unit and free standard shipping.
      </p>
      <div className="mt-8 grid gap-4 lg:grid-cols-2" data-id="element-843" data-mp="3rswpd65sk8ai">
        <a href="#offer" data-buy-intent="true" onClick={event => {
        event.preventDefault();
        requestOffer('crew');
        trackCustom('mini_funnel_cta_clicked', {
          pack: 'crew'
        });
      }} className="border border-white/15 bg-ink-900 p-6 transition-colors duration-150 ease-grit hover:border-grit-500" data-id="element-844" data-mp="3rs4w41zo2mne">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-845" data-mp="393gafdtd0dak">Buy 4, get 2 free</p>
          <p className="mt-2 font-display text-3xl font-extrabold uppercase text-offwhite" data-id="element-846" data-mp="3e1rjvnfqsls9">6 X1 · $179.90</p>
          <p className="mt-1 text-[14px] text-steel-300" data-id="element-847" data-mp="3i1jz2izn5q6l">$29.98 per X1 · 18 lenses</p>
        </a>
        <a href="#company" data-buy-intent="true" onClick={event => {
        event.preventDefault();
        trackCustom('mini_funnel_cta_clicked', {
          pack: 'business'
        });
        scrollToCompany();
      }} className="border-2 border-grit-500 bg-grit-500/10 p-6 transition-colors duration-150 ease-grit hover:bg-grit-500/15" data-id="element-848" data-mp="3muuu7mv3ciio">
          <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-grit-500" data-id="element-849" data-mp="2hv6cvylsw8v7">
            <BuildingIcon className="h-4 w-4" aria-hidden="true" data-id="element-850" data-mp="2dv60skm6c9rb" />
            Business / crew pack
          </p>
          <p className="mt-2 font-display text-3xl font-extrabold uppercase text-offwhite" data-id="element-851" data-mp="28wb6aleugwrd">30 X1 · $699.90</p>
          <p className="mt-1 text-[14px] text-steel-300" data-id="element-852" data-mp="1z45jdq3g95j1">Buy 20, get 10 free · 90 lenses · one order</p>
        </a>
      </div>
    </>;
}
const closingCta: Partial<Record<ObjectionId, {
  pack: PackId;
  cta: string;
}>> = {
  battery: {
    pack: 'trio',
    cta: 'See multi-X1 options'
  },
  fit: {
    pack: 'single',
    cta: 'Choose your X1'
  },
  warranty: {
    pack: 'trio',
    cta: 'Choose your coverage'
  }
};

/**
 * One dynamic recovery area, hidden until the visitor tells us what is holding
 * them back. Only the matching answer renders, so the normal page stays clean.
 */
export function ObjectionRecovery({
  active,
  onChooseAnother
}: ObjectionRecoveryProps) {
  const choice = active ? objectionChoices.find(item => item.id === active) : undefined;
  useEffect(() => {
    if (choice) trackCustom('mini_funnel_viewed', {
      objection: choice.id
    });
  }, [choice]);
  const close = choice ? closingCta[choice.id] : undefined;
  return <AnimatePresence initial={false} data-id="element-853" data-mp="2lazmleq88ax0">
      {choice && <motion.section id={RECOVERY_ID} key={choice.id} aria-labelledby="recovery-heading" className="scroll-mt-28 border-b border-white/10 bg-ink-950" initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 12
    }} transition={{
      duration: 0.24,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-854" data-mp="2l9es1p3wwrz5">
          <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20" data-id="element-855" data-mp="22eq7qp7i3gby">
            <p className="inline-flex flex-wrap items-center gap-2 border border-grit-500/60 bg-grit-500/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-grit-500" data-id="element-856" data-mp="27ayilqeo0ndb">
              You asked about:
              <span className="text-offwhite" data-id="element-857" data-mp="1it7vwy9p7i6z">{choice.label}</span>
            </p>

            <div id="recovery-heading" className="mt-6" data-id="element-858" data-mp="1xpfghzp0qlms">
              {choice.id === 'price' && <PriceFunnel data-id="element-859" data-mp="1sngb6k9pxen7" />}
              {choice.id === 'prescription' && <PrescriptionFunnel data-id="element-860" data-mp="26oajz40ohhme" />}
              {choice.id === 'battery' && <BatteryFunnel data-id="element-861" data-mp="26mfztrx8dtjd" />}
              {choice.id === 'fit' && <FitFunnel data-id="element-862" data-mp="21oo5kr945vc0" />}
              {choice.id === 'shipping' && <ShippingFunnel data-id="element-863" data-mp="2gelzzoxmzh9n" />}
              {choice.id === 'warranty' && <WarrantyFunnel data-id="element-864" data-mp="2ge2fq1kikgsg" />}
              {choice.id === 'pack' && <PackRecommender data-id="element-865" data-mp="2bezfqvje50rc" />}
              {choice.id === 'crew' && <CrewFunnel data-id="element-866" data-mp="2bhlsrap1w8gy" />}
            </div>

            {close && <PackClose recommended={close.pack} cta={close.cta} data-id="element-867" data-mp="2pgbs6wzcek8h" />}

            <button type="button" onClick={onChooseAnother} className="mt-8 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-steel-400 transition-colors duration-150 ease-grit hover:text-grit-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-868" data-mp="2ldyp6gbzd0fe">
              <RefreshCwIcon className="h-4 w-4" aria-hidden="true" data-id="element-869" data-mp="36wbrj9dalgrl" />
              Something else? Choose another
            </button>
          </div>
        </motion.section>}
    </AnimatePresence>;
}